export enum MESSAGE_TYPE {
  USER = "user",
  ASSISTANT = "assistant",
}

export enum MESSAGE_STATE {
  PROGRESS = "progress",
  TOKEN = "token",
  DONE = "done",
  ERROR = "error",
}

// Byte-for-byte aligned with the backend's ProcessingStep enum (api/app/interfaces/Chat.interface.ts)
// and the DIRECT_EVENTS set the SSE handler writes as their own wire event (api/app/controllers/chat.controller.ts).
export enum StreamEventType {
  Token = "token",
  Progress = "progress",
  FinalAnswer = "final_answer",
  TitleGenerated = "title_generated",
  MessageSaved = "message_saved",
  Usage = "usage",
  QuotaUpdated = "quota_updated",
  // api R06 add-on: fired right after the chat is created/loaded and the question saved, well
  // before generation -- the earliest point a chat_uuid exists for the server-side Stop endpoint.
  ChatReady = "chat_ready",
  // api R09/R13 add-on: fires after message_saved and before done, once the api has generated
  // suggested follow-up questions for this answer.
  FollowUpsGenerated = "follow_ups_generated",
  Done = "done",
  Error = "error",
}

/** api R09 add-on: how retrieval went for this answer -- "matched" (cited sources), "no_match"
 *  (searched, nothing relevant found), "none" (no retrieval attempted), or null for a pre-R09
 *  message that predates the field. Drives StatusLine's "no match" variant and the Chat/NotFound
 *  layout (phase-r14). */
export type ChatRetrieval = "matched" | "no_match" | "none";

/** Citation source for an assistant answer. procedureId is camelCase on the wire (documentSourceSchema).
 *  `ref`/`cited`/`title`/`team`/`updatedAt` are api R08/R09 add-ons -- a pre-R10 message's sources have
 *  none of them (phase-r10 §3 "old messages" fallback, helpers/citations.ts). Hidden procedures come
 *  without `procedureId`/`filePath`. */
export interface MessageSource {
  fileName: string;
  filePath?: string;
  procedureId?: number;
  score: number;
  chunkText?: string;
  /** Join key back to the answer text's `[n]` markers -- NOT the display number shown on the card or
   *  in the text (helpers/citations.ts renumbers by first-citation order). */
  ref?: number;
  /** True when the model actually cited this source. Only `cited` sources render as source cards. */
  cited?: boolean;
  title?: string;
  team?: string;
  /** ISO timestamp. */
  updatedAt?: string;
}

// api R11 — mirrors the api's feedback_reason union exactly (923-core-context context/engineering/procedure-assistant/chat-redesign
// /phase-r11-api-feedback-reason.md). A discriminated set of values, not a free string, so an invalid
// reason can never round-trip through the wire -- Chat/ReasonChip's four labels map 1:1 (constants/index.tsx).
export type FeedbackReason = "out_of_date" | "wrong_procedure" | "did_not_answer" | "something_else";

export interface ChatMessage {
  id: number;
  uuid: string;
  message: string;
  is_liked: boolean | null;
  feedback: string | null;
  /** Only ever non-null when is_liked is false -- the api rejects a reason on any other request
   *  (api R11). Cleared server-side by liking or un-rating. */
  feedback_reason?: FeedbackReason | null;
  type: MESSAGE_TYPE;
  created_at: string;
  sources?: MessageSource[] | null;
  /** phase-r14: null for a pre-R09 message. */
  retrieval?: ChatRetrieval | null;
  /** phase-r14: suggested next questions, generated after the answer is saved -- null until the
   *  follow_ups_generated event arrives (or for a pre-R09 message that predates the feature). */
  follow_ups?: string[] | null;
  /** phase-f1: true when the user stopped generation before the answer finished (api R06's Stop
   *  endpoint, already on the wire since then). A stopped turn renders "Stopped" in place of
   *  "Searching procedures"/"Searched procedures" (StatusLine's "stopped" variant), even with no text
   *  yet. Undefined for a message from before this field existed. */
  is_stopped?: boolean;
}

export interface IChat {
  id: number;
  uuid: string;
  user_id: number;
  title: string;
  is_processing: boolean;
  created_at: string;
  messages: ChatMessage[];
}

export interface ChatListItem {
  id: number;
  uuid: string;
  title: string;
  is_processing: boolean;
  created_at: string;
  message_count: number;
}

export interface ChatStarter {
  id: number;
  question: string;
  /** Procedure area label (e.g. "Time off"), or null when the starter has none (api R03). Optional
   *  because the pre-R03 api omits this key entirely -- it is `undefined` at runtime there, which
   *  StarterCard treats the same as null (no category row). */
  category?: string | null;
}

export interface TokenUsage {
  prompt: number;
  completion: number;
  total: number;
  cachedPrompt?: number;
  reasoningTokens?: number;
}

export interface TokenQuota {
  enabled: boolean;
  monthlyRemaining: number;
  monthlyLimit: number;
  monthlyUsed: number;
  bucketRemaining: number;
  bucketCapacity: number;
  periodStart: string;
}

export interface ChatsFilter {
  page: number;
  limit: number;
  search?: string;
}

/** GET /chat's real response shape — {results, total}, NOT the shared PaginatedResponse's {rows, count}. */
export interface ChatListResponse {
  results: ChatListItem[];
  total: number;
  is_clear?: boolean;
}

export interface ISendMessageRequest {
  message: string;
  chat_uuid?: string;
}

export interface IUpdateChatDto {
  uuid: string;
  title: string;
}

export interface IDeleteChatSuccess {
  uuid: string;
}

export interface IReactToMessageDto {
  chatUuid: string;
  messageUuid: string;
  is_liked?: boolean | null;
  feedback?: string | null;
  feedback_reason?: FeedbackReason | null;
}

// --- Streamed SSE event payloads, mirroring the backend's StreamEventData exactly ---

export interface IStreamTokenEvent {
  token: string;
}

export interface IStreamProgressEvent {
  step: string;
  displayMessage: string;
}

export interface IStreamFinalAnswerEvent {
  answer: string;
  chat_uuid: string;
  chat_id: number;
  sources?: MessageSource[];
}

export interface IStreamTitleGeneratedEvent {
  title: string;
}

/** api R06 add-on: the chat this question belongs to, reported before generation starts. */
export interface IStreamChatReadyEvent {
  chat_uuid: string;
}

/** api R09/R13 add-on: suggested follow-up questions for the message they name -- a direct SSE
 *  event, not part of message_saved's payload (phase-r14 §3). */
export interface IStreamFollowUpsGeneratedEvent {
  message_uuid: string;
  follow_ups: string[];
}

export interface IStreamMessageSavedEvent {
  chat_uuid: string;
  message: ChatMessage;
}

export interface IStreamUsageEvent {
  usage: TokenUsage;
}

export interface IStreamQuotaUpdatedEvent {
  quota: TokenQuota;
}

export interface IStreamErrorEvent {
  message?: string;
}

/** Client-side message shape used while a conversation is being composed/streamed, before or in place of a persisted ChatMessage. */
export interface StreamingMessage {
  id: string;
  uuid?: string;
  state: MESSAGE_STATE;
  type: MESSAGE_TYPE;
  message: string;
  is_liked?: boolean | null;
  feedback?: string | null;
  feedback_reason?: FeedbackReason | null;
  created_at?: string;
  sources?: MessageSource[] | null;
  retrieval?: ChatRetrieval | null;
  follow_ups?: string[] | null;
  /** phase-f1: see {@link ChatMessage.is_stopped} -- spread straight through by convertSavedMessages
   *  on hydration, so a reloaded stopped turn keeps its label. */
  is_stopped?: boolean;
}

export interface IStreamCallbacks {
  onToken: (token: string) => void;
  onProgress: (step: string, displayMessage: string) => void;
  onFinalAnswer?: (data: IStreamFinalAnswerEvent) => void;
  onMessageSaved?: (data: IStreamMessageSavedEvent) => void;
  onChatReady?: (data: IStreamChatReadyEvent) => void;
  onFollowUpsGenerated?: (data: IStreamFollowUpsGeneratedEvent) => void;
  onTitleGenerated?: (title: string) => void;
  onUsage?: (usage: TokenUsage) => void;
  onQuotaUpdated?: (data: IStreamQuotaUpdatedEvent) => void;
  onDone: () => void;
  onError: (error: Error) => void;
}
